import katex from 'katex';
import 'katex/dist/katex.min.css';
// Chemistry support: enables \ce{...} / \pu{...} (mhchem) in formulas.
import 'katex/dist/contrib/mhchem';

/**
 * MathLive-specific commands KaTeX does not know. Formulas authored in
 * the visual editor can carry them (its virtual keyboard emits e.g.
 * \exponentialE for the "e" constant); without these macros KaTeX
 * renders the command as red error text. New inserts are macro-expanded
 * at the source (see MathModal), but already-saved content still needs
 * the compatibility layer at render time.
 */
const MATHLIVE_COMPAT_MACROS: Record<string, string> = {
	'\\exponentialE': '\\mathrm{e}',
	'\\imaginaryI': '\\mathrm{i}',
	'\\imaginaryJ': '\\mathrm{j}',
	'\\differentialD': '\\mathrm{d}',
	'\\capitalDifferentialD': '\\mathrm{D}',
	'\\placeholder': '{#1}',
};

type KatexOptions = { macros?: Record<string, string> } & Record<
	string,
	unknown
>;

const withCompatMacros = (options?: KatexOptions): KatexOptions => ({
	...options,
	macros: { ...MATHLIVE_COMPAT_MACROS, ...options?.macros },
});

/**
 * Quill's built-in formula blot renders through window.katex; it must be
 * present before any content containing formulas is seeded, so this
 * module is imported eagerly by the package entry. The wrapper injects
 * the MathLive compatibility macros into every render call (mhchem is
 * already patched into the underlying katex object by its import).
 */
(window as unknown as { katex: unknown }).katex = {
	...katex,
	render: (
		expression: string,
		element: HTMLElement,
		options?: KatexOptions
	) => katex.render(expression, element, withCompatMacros(options) as never),
	renderToString: (expression: string, options?: KatexOptions) =>
		katex.renderToString(expression, withCompatMacros(options) as never),
};
